'use client'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'
import { mq, vw, designGrid, font40_normal, font30_normal } from '@/styles'
import { useTranslator } from '@/hooks'
import Link from 'next/link'

type Props = {
  inner?: boolean
}

const fallbackText = {
  title: 'The page you are looking for does not exist.',
  description: 'Try navigating the web to see if you can find it. Or maybe you discover something even better, who knows.',
  goback_homepage: 'Go back to Homepage'
}

const getText = (t, key) => {
  if (!t || typeof t !== 'function') return fallbackText[key]
  const str = 'page_not_found.' + fallbackText[key]
  const text = t(str)
  if(typeof text === 'string' && text.length > 0) return text
  return fallbackText[key]
}

export const SectionNotFound:FC<Props> = memo(() => {
  const { t } = useTranslator() || {}
  const title = useMemo(() => getText(t, 'title'), [t])
  const description = useMemo(() => getText(t, 'description'), [t])
  const gohomepage = useMemo(() => getText(t, 'goback_homepage'), [t])

  return (
    <Section>
      <hgroup>
        <h1>{title}</h1>
        <p>{description}</p>
        <Link href='/' className='btn'>
          {gohomepage}
        </Link>
      </hgroup>
    </Section>
  )
})

const Section = styled.section`
  ${designGrid({})}
  margin: 140px auto 0;
  max-width: 1700px;

  ${mq.greaterThan('desktop-xs')} {
    margin: 80px auto 0;
  }

  hgroup {
    grid-column:  1 / span 6;
    padding: ${vw(60, 'mobile')} 0;
    display: flex;
    flex-direction: column;

    ${mq.greaterThan('tablet')} {
      grid-column:  3 / span 8;
      padding: ${vw(80, 'desktop')} 0 ${vw(160, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 80px 0 160px;
    }
  }

  h1 {
    ${font40_normal(true)}
    margin-bottom: ${vw(20, 'mobile')};

    ${mq.greaterThan('tablet')} {
      ${font40_normal(false)}
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }
  }

  p {
    ${font30_normal(true)}
    text-align: center;
    margin-bottom: ${vw(40, 'mobile')};

    ${mq.greaterThan('tablet')} {
      ${font30_normal(false)}
      margin-bottom: ${vw(40, 'desktop')};
    }
  }

  .btn {
    background-color: var(--color-text);
    border: 1px solid var(--color-text);
    color: var(--color-background);
    padding: 15px 30px;
    transition: 300ms background-color linear, 300ms color linear;
    margin: 0 auto;

    ${mq.greaterThan('tablet')} {
      padding: ${vw(15, 'desktop')} ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 15px 30px;
    }

    &:hover {
      border: 1px solid #000;
      background-color: #000;
      color: var(--color-background);
    }
  }
`
